import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Upload, Command } from 'lucide-react'
import { useSearch } from '@/hooks/useSearch'
import { supabase, isSupabaseConfigured } from '@/lib/supabase'
import { Input } from '@/components/ui/input'

type IngestStatus = 'idle' | 'ingesting' | 'success' | 'error'

interface IntentBarProps {
  onToggleSidebar: () => void
  sidebarOpen: boolean
  onFocusChange: (focused: boolean) => void
}

type IntentMode = 'search' | 'question' | 'command' | 'ingest'

export default function IntentBar({
  onToggleSidebar,
  onFocusChange
}: IntentBarProps) {
  const [input, setInput] = useState('')
  const [mode, setMode] = useState<IntentMode>('search')
  const [focused, setFocused] = useState(false)

  const [stimulusSuggestions, setStimulusSuggestions] = useState<string[]>([])
  const [ingestStatus, setIngestStatus] = useState<IngestStatus>('idle')
  const [ingestMessage, setIngestMessage] = useState('')

  const inputRef = useRef<HTMLInputElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()
  const { results: suggestions, isSearching: loading, search } = useSearch()

  // Load stimulus suggestions from recent concepts
  const loadStimulusSuggestions = useCallback(async () => {
    if (!isSupabaseConfigured()) return
    try {
      const { data } = await supabase
        .from('concepts')
        .select('name, domain:domains(name)')
        .order('synapse_strength', { ascending: true })
        .limit(5)

      if (data && data.length > 0) {
        const prompts = data.map((c: { name: string; domain: { name: string }[] | null }) => {
          const domain = c.domain?.[0]?.name || ''
          return `Como ${c.name} se conecta com ${domain}?`
        })
        setStimulusSuggestions(prompts)
      } else {
        setStimulusSuggestions([
          'O que está na sua mente?',
          'Cole um link para ingerir conhecimento',
          'Busque conexões entre conceitos',
        ])
      }
    } catch {
      setStimulusSuggestions([
        'O que está na sua mente?',
        'Cole um link para ingerir conhecimento',
        'Busque conexões entre conceitos',
      ])
    }
  }, [])

  useEffect(() => {
    loadStimulusSuggestions()
  }, [loadStimulusSuggestions])

  // Auto-detect intent based on input
  useEffect(() => {
    const detectIntent = (text: string) => {
      if (text.startsWith('?') || text.toLowerCase().includes('como ') || text.toLowerCase().includes('por que ')) {
        return 'question'
      }
      if (text.startsWith('/') || text.toLowerCase().includes('cmd:')) {
        return 'command'
      }
      if (text.startsWith('http') || text.includes('ingest:')) {
        return 'ingest'
      }
      return 'search'
    }

    if (input) {
      const newMode = detectIntent(input)
      if (newMode !== mode) {
        setMode(newMode)
      }
    }
  }, [input, mode])

  // Listen for custom 'intent-bar:focus' events dispatched by other components
  useEffect(() => {
    const handleIntentFocus = (e: Event) => {
      const detail = (e as CustomEvent<{ prefill?: string }>).detail
      if (detail?.prefill != null) {
        setInput(detail.prefill)
      }
      inputRef.current?.focus()
    }

    window.addEventListener('intent-bar:focus', handleIntentFocus)
    return () => window.removeEventListener('intent-bar:focus', handleIntentFocus)
  }, [])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        inputRef.current?.focus()
      }
      if (e.key === 'Escape') {
        inputRef.current?.blur()
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
        e.preventDefault()
        onToggleSidebar()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onToggleSidebar])

  const handleFocus = () => {
    setFocused(true)
    onFocusChange(true)
  }

  const handleBlur = () => {
    setFocused(false)
    onFocusChange(false)
  }

  // Debounced suggestions
  useEffect(() => {
    if (mode === 'search' && input && focused && input.length >= 2) {
      const timeoutId = setTimeout(() => {
        search(input, { limit: 5 })
      }, 300)
      return () => clearTimeout(timeoutId)
    }
  }, [input, mode, focused, search])

  const handleIngest = async (text: string) => {
    // ... (Keep existing logic)
    if (!isSupabaseConfigured()) {
      setIngestStatus('error')
      setIngestMessage('Supabase não configurado. Vá em Configurações.')
      return
    }

    setIngestStatus('ingesting')
    setIngestMessage('Criando ingestão...')

    try {
      const isUrl = /^https?:\/\//i.test(text.trim())
      const contentType = isUrl ? 'link' : 'text'

      const record: Record<string, unknown> = {
        content_type: contentType,
        raw_content: isUrl ? null : text.trim(),
        source_url: isUrl ? text.trim() : null,
        source_title: isUrl ? text.trim().slice(0, 100) : (text.trim().slice(0, 60) + (text.length > 60 ? '...' : '')),
        status: 'pending',
        ingested_at: new Date().toISOString(),
        atom_count: 0,
        concept_count: 0,
        principle_count: 0,
      }

      const { data, error } = await supabase
        .from('ingestions')
        .insert(record)
        .select('id')
        .single()

      if (error) throw error

      setIngestStatus('success')
      setIngestMessage(`Ingestão criada! ID: ${data.id.slice(0, 8)}...`)
      setInput('')

      setTimeout(() => {
        setIngestStatus('idle')
        setIngestMessage('')
      }, 3000)
    } catch (err: unknown) {
      setIngestStatus('error')
      setIngestMessage(err instanceof Error ? err.message : 'Erro ao ingerir conteúdo')
      setTimeout(() => {
        setIngestStatus('idle')
        setIngestMessage('')
      }, 4000)
    }
  }

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    // ... (Keep existing logic)
     const file = e.target.files?.[0]
    if (!file || !isSupabaseConfigured()) return

    // Limit file size to 10MB
    if (file.size > 10 * 1024 * 1024) {
      setIngestStatus('error')
      setIngestMessage('Arquivo muito grande (máx 10MB)')
      setTimeout(() => { setIngestStatus('idle'); setIngestMessage('') }, 4000)
      e.target.value = ''
      return
    }

    setIngestStatus('ingesting')
    setIngestMessage(`Lendo ${file.name}...`)

    const ext = file.name.split('.').pop()?.toLowerCase() || ''
    const typeMap: Record<string, string> = {
      pdf: 'pdf', mp3: 'audio', wav: 'audio', m4a: 'audio',
      mp4: 'video', webm: 'video', txt: 'text'
    }
    const contentType = typeMap[ext] || 'text'

    try {
      // Read file content
      let rawContent: string | null = null
      if (contentType === 'text') {
        rawContent = await file.text()
      } else {
        // For binary files, store as base64
        const buffer = await file.arrayBuffer()
        const bytes = new Uint8Array(buffer)
        let binary = ''
        for (let i = 0; i < bytes.length; i++) {
          binary += String.fromCharCode(bytes[i])
        }
        rawContent = `data:${file.type};base64,${btoa(binary)}`
      }

      setIngestMessage(`Enviando ${file.name}...`)

      const { data, error } = await supabase
        .from('ingestions')
        .insert({
          content_type: contentType,
          source_title: file.name,
          raw_content: rawContent,
          status: 'pending',
          ingested_at: new Date().toISOString(),
          atom_count: 0,
          concept_count: 0,
          principle_count: 0,
        })
        .select('id')
        .single()

      if (error) throw error

      setIngestStatus('success')
      setIngestMessage(`Arquivo "${file.name}" enviado! ID: ${data.id.slice(0, 8)}...`)
      setTimeout(() => {
        setIngestStatus('idle')
        setIngestMessage('')
      }, 3000)
    } catch (err: unknown) {
      setIngestStatus('error')
      setIngestMessage(err instanceof Error ? err.message : 'Erro ao enviar arquivo')
      setTimeout(() => {
        setIngestStatus('idle')
        setIngestMessage('')
      }, 4000)
    }

    e.target.value = ''
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!input.trim()) return

    switch (mode) {
      case 'search':
        navigate(`/constellation`)
        break
      case 'question':
        navigate(`/constellation`)
        break
      case 'command':
        handleCommand(input)
        break
      case 'ingest':
        await handleIngest(input)
        break
    }
  }

  const handleCommand = (command: string) => {
    const cmd = command.replace(/^\/|cmd:\s*/i, '').toLowerCase()
    switch (cmd) {
      case 'pulse': navigate('/'); break
      case 'constellation': navigate('/constellation'); break
      case 'settings': navigate('/settings'); break
      default: break
    }
    setInput('')
  }

  const getPlaceholder = () => {
    switch (mode) {
      case 'question': return 'What is your question?'
      case 'command': return 'Enter command (/pulse, /network)'
      case 'ingest': return 'Paste link or text to ingest...'
      default: return 'Search your second brain...'
    }
  }

  const getModeIcon = () => {
    switch (mode) {
      case 'question': return <Search className="w-5 h-5 text-rsb-muted" />
      case 'command': return <Command className="w-5 h-5 text-rsb-muted" />
      case 'ingest': return <Upload className="w-5 h-5 text-rsb-muted" />
      default: return <Search className="w-5 h-5 text-rsb-muted" />
    }
  }

  return (
    <div className="w-full flex justify-center pt-4 px-4 relative z-50">
      <div className="w-full max-w-2xl relative">
        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.mp3,.mp4,.wav,.m4a,.webm,.txt"
          className="hidden"
          onChange={handleFileUpload}
          aria-label="Upload file"
        />

        <form onSubmit={handleSubmit} className="relative group">
          {/* Glow effect */}
          <div className={`absolute -inset-0.5 bg-gradient-to-r from-rsb-accent to-domain-tech rounded-2xl blur opacity-20 group-hover:opacity-40 transition duration-1000 group-hover:duration-200 ${focused ? 'opacity-60' : ''}`}></div>

          <div className={`
            relative flex items-center rounded-2xl transition-all duration-300
            ${focused
              ? 'bg-[#0A0A0F] ring-1 ring-white/10 shadow-2xl'
              : 'bg-[#0A0A0F]/80 backdrop-blur-md border border-white/5 hover:border-white/10'
            }
          `}>
            {/* Mode Icon */}
            <div className="pl-4 flex items-center justify-center">
              {getModeIcon()}
            </div>

            <Input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onFocus={handleFocus}
              onBlur={handleBlur}
              placeholder={getPlaceholder()}
              className="w-full pl-3 pr-20 py-4 h-auto bg-transparent text-white placeholder-rsb-muted focus-visible:ring-0 focus-visible:ring-offset-0 border-0 shadow-none font-medium text-lg md:text-lg"
              autoComplete="off"
            />

            {/* Right Actions */}
            <div className="absolute right-3 flex items-center gap-2">
              {ingestStatus === 'ingesting' ? (
                <div className="w-5 h-5 loading-spinner border-2 border-rsb-accent" />
              ) : loading ? (
                <div className="w-5 h-5 loading-spinner border-2" />
              ) : (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="p-2 rounded-lg hover:bg-white/10 text-rsb-muted hover:text-white transition-colors"
                    title="Upload"
                  >
                    <Upload className="w-5 h-5" />
                  </button>
                  <div className="h-4 w-px bg-white/10 mx-1"></div>
                  <div className="hidden sm:flex items-center gap-1 text-[10px] text-rsb-muted font-mono bg-white/5 px-2 py-1 rounded">
                    ⌘K
                  </div>
                </div>
              )}
            </div>
          </div>
        </form>

        {/* Status Message */}
        {ingestStatus !== 'idle' && (
          <div className={`absolute top-full left-0 right-0 mt-2 p-3 rounded-xl text-sm backdrop-blur-md border animate-fade-in ${
            ingestStatus === 'success' ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' :
            ingestStatus === 'error' ? 'bg-red-500/10 border-red-500/20 text-red-400' :
            'bg-rsb-accent/10 border-rsb-accent/20 text-rsb-accent'
          }`}>
             <span className="mr-2">{ingestStatus === 'success' ? '✅' : ingestStatus === 'error' ? '❌' : '⏳'}</span>
             {ingestMessage}
          </div>
        )}

        {/* Suggestions Dropdown */}
        {(focused && (stimulusSuggestions.length > 0 || suggestions.length > 0)) && (
          <div
            className="absolute top-full left-0 right-0 mt-3 bg-[#0A0A0F]/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/10 overflow-hidden animate-slide-in origin-top z-[100]"
            onMouseDown={(e) => e.preventDefault()} // Prevent blur
          >
            {input && suggestions.length > 0 ? (
              <div className="p-2">
                 {suggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setInput('')
                        const nodeType = suggestion.node_type === 'concept' ? 'concept' : 'auto'
                        navigate(`/dive/${suggestion.node_id}?type=${nodeType}`)
                      }}
                      className="w-full text-left p-3 rounded-xl hover:bg-white/5 transition-all group flex items-start gap-4"
                    >
                      <div className="mt-1 p-2 rounded-lg bg-white/5 group-hover:bg-rsb-accent/20 transition-colors">
                        {suggestion.node_type === 'concept' ? '💭' : '📄'}
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-white group-hover:text-rsb-accent transition-colors">
                          {suggestion.content}
                        </div>
                        <div className="text-xs text-rsb-muted mt-0.5 flex items-center gap-2">
                          <span className="uppercase tracking-wider">{suggestion.node_type}</span>
                          <span className="w-1 h-1 rounded-full bg-white/20"></span>
                          <span>{Math.round(suggestion.similarity * 100)}% match</span>
                        </div>
                      </div>
                    </button>
                 ))}
              </div>
            ) : !input && (
              <div className="p-4">
                <div className="text-[10px] text-rsb-muted uppercase tracking-widest mb-3 font-display">Suggested Stimuli</div>
                <div className="space-y-2">
                  {stimulusSuggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      onClick={() => setInput(suggestion)}
                      className="w-full text-left px-4 py-3 rounded-xl bg-white/5 hover:bg-white/10 hover:translate-x-1 transition-all text-sm text-rsb-text-secondary hover:text-white border border-transparent hover:border-white/5"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
